import GridContainer from "@/components/common/GridContainer"
import { getAspectRatioFromImage, getCssSizeValue, getHtmlFromRedactorField, getPaddingsClassFromData } from "@/utils/utils"
import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import Image from "@/components/common/Image"
import Text from "@/components/common/Text"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { motion } from "framer-motion"
import { gsap } from "@/utils/gsap"
import { useGSAP } from "@gsap/react"
import { useIsMobile } from "@/hooks/useDevice"
import HeaderDescriptionBlock from "@/components/HeaderDescriptionBlock"

const StyledContentWrapper = styled.div`
	display: flex;
	gap: var(--paddingX);

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet || 768}px) {
		gap: unset;
	}
`

const StyledImage = styled(Image)`
	display: none;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet || 768}px) {
		display: block;
		height: auto;
		aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || "16/9"};
		width: 100%;
	}
`

const StyledImageWrapper = styled.div`
	display: none;
	@media (min-width: ${({ theme }) => theme.breakpoints.tablet || 768}px) {
		display: block;
		width: 50%;
		position: relative;
		overflow: hidden;
	}
`

const StyledContentAndNavigation = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	background: ${({ theme }) => theme.colors.platinum};
	padding: ${getCssSizeValue(30, BREAKPOINTS.MOBILE)} ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
	width: 100%;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		width: 50%;
		padding: ${getCssSizeValue(30, BREAKPOINTS.TABLET)} ${getCssSizeValue(40, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(40)} ${getCssSizeValue(120)};
	}

	.header {
		margin-bottom: ${getCssSizeValue(32)};
	}
`

const StyledNavigation = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${getCssSizeValue(12, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		gap: ${getCssSizeValue(16, BREAKPOINTS.TABLET)};
		flex-direction: row;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		gap: ${getCssSizeValue(12)};
	}
`

const StyledSingleNavigationItem = styled.div`
	--size: ${getCssSizeValue(50, BREAKPOINTS.MOBILE)};
	cursor: pointer;
	position: relative;
	overflow: hidden;
	display: grid;
	place-items: center;
	height: var(--size);
	width: var(--size);
	background: ${({ theme }) => theme.colors.richBlue};
	color: ${({ theme }) => theme.colors.antiflashWhite};
	transition: all 0.2s;

	&.active {
		background: ${({ theme }) => theme.colors.chilliRed};
	}

	.number {
		position: relative;
		z-index: 3;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		--size: ${getCssSizeValue(50, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		--size: ${getCssSizeValue(50)};

		&:hover::after {
			transform: translateY(0);
		}

		&::after {
			content: "";
			transform: translateY(100%);
			transition: all 0.2s;
			position: absolute;
			background: ${({ theme }) => theme.colors.chilliRed};
			height: 100%;
			width: 100%;
			top: 0;
			left: 0;
		}
	}
`

const CurtainOverlay = styled.div`
	position: absolute;
	z-index: 1;
	width: 100%;
	height: calc(50% + 1px);
	pointer-events: none;

	&.first-top,
	&.second-top {
		top: 0;
		left: 0;
		background: ${({ theme }) => theme.colors.periwinkle};
		transform-origin: center bottom;
	}

	&.second-top {
		background: lightgoldenrodyellow;
	}

	&.first-bottom,
	&.second-bottom {
		top: 50%;
		left: 0;
		background: ${({ theme }) => theme.colors.periwinkle};
		transform-origin: center bottom;
	}

	&.second-bottom {
		background: lightgoldenrodyellow;
	}
`

export default function GalleryDescriptionTextModule({ data }) {
	const isMobile = useIsMobile()
	const paddings = getPaddingsClassFromData(data)
	const items = data?.items || []
	const [currentItem, setCurrentItem] = useState(items[0] || null)
	const aspectRatio = getAspectRatioFromImage(currentItem?.image?.image)
	const containerRef = useRef(null)
	const [isAnimating, setIsAnimating] = useState(false)

	const handleSetCurrentItem = (item) => {
		if (isAnimating) return
		if (item === currentItem) return

		triggerWaveAnimation(() => {
			setCurrentItem(item)
		})
	}

	const { contextSafe } = useGSAP({ scope: containerRef })

	const triggerWaveAnimation = contextSafe((onComplete) => {
		const tl = gsap.timeline({
			paused: true,
			defaults: {
				duration: 2,
			},
			onStart: () => setIsAnimating(true),
			onComplete: () => setIsAnimating(false),
		})

		tl.to(".first-top", {
			keyframes: {
				"0%": { scaleY: 0, transformOrigin: "center bottom" },
				"50%": {
					scaleY: 1,
					transformOrigin: "center bottom",
					onComplete: () => {
						onComplete()
						gsap.set(".scale-image", {
							scale: 1.04,
						})
					},
				},
				"100%": { scaleY: 0, transformOrigin: "center top" },
			},
			duration: 1,
		})
			.to(
				".first-bottom",
				{
					keyframes: {
						"0%": { scaleY: 0, transformOrigin: "center top" },
						"50%": { scaleY: 1, transformOrigin: "center top" },
						"100%": { scaleY: 0, transformOrigin: "center bottom" },
					},
					duration: 1,
				},
				"<",
			)
			.to(
				".header",
				{
					keyframes: {
						"0%": { x: 0, opacity: 1 },
						"50%": { x: -30, opacity: 0 },
						"100%": { x: 0, opacity: 1 },
					},
					duration: 1,
				},
				"<",
			)
			.to(
				".description",
				{
					keyframes: {
						"0%": { x: 0, opacity: 1 },
						"50%": { x: -30, opacity: 0 },
						"100%": { x: 0, opacity: 1 },
					},
					duration: 1,
				},
				"<",
			)
			.to(
				".second-top",
				{
					keyframes: {
						"0%": { scaleY: 0, transformOrigin: "center bottom" },
						"50%": { scaleY: 1, transformOrigin: "center bottom" },
						"100%": { scaleY: 0, transformOrigin: "center top" },
					},
					ease: "sine.in",
					duration: 0.8,
				},
				"-=99%",
			)
			.to(
				".second-bottom",
				{
					keyframes: {
						"0%": { scaleY: 0, transformOrigin: "center top" },
						"50%": { scaleY: 1, transformOrigin: "center top" },
						"100%": { scaleY: 0, transformOrigin: "center bottom" },
					},
					ease: "sine.in",
					duration: 0.8,
				},
				"<",
			)
			.to(
				".scale-image",
				{
					scale: 1,
					duration: 0.4,
					ease: "power1.in",
				},
				"-=90%",
			)

		tl.play()
	})

	useEffect(() => {
		gsap.set(".curtain", { scaleY: 0 })
	}, [])

	return (
		<div className={paddings}>
			<HeaderDescriptionBlock headerDescription={data?.headerDescription} />
			<GridContainer className={`indent-desktop-1`} ref={containerRef}>
				<StyledContentWrapper className="col-desktop-14 col-tablet-8 col-mobile-8">
					{isMobile && <Navigation items={items} currentItem={currentItem} handleSetCurrentItem={handleSetCurrentItem} />}
					<StyledImageWrapper>
						<StyledImage
							src={currentItem?.image?.image?.url}
							alt={currentItem?.image?.image?.alt}
							sizes={"100vw"}
							$aspectRatio={aspectRatio}
							className="scale-image"
						/>
						<CurtainOverlay className="curtain first-top" />
						<CurtainOverlay className="curtain first-bottom" />
						<CurtainOverlay className="curtain second-top" />
						<CurtainOverlay className="curtain second-bottom" />
					</StyledImageWrapper>
					<StyledContentAndNavigation>
						<span />
						<div>
							<Text
								as={motion.div}
								dangerouslySetInnerHTML={getHtmlFromRedactorField(currentItem?.header || "")}
								className="header typo-borna-36"
							/>
							<Text
								as={motion.div}
								dangerouslySetInnerHTML={getHtmlFromRedactorField(currentItem?.description || "")}
								className="description typo-borna-38"
							/>
						</div>
						{!isMobile && <Navigation items={items} currentItem={currentItem} handleSetCurrentItem={handleSetCurrentItem} />}
					</StyledContentAndNavigation>
				</StyledContentWrapper>
			</GridContainer>
		</div>
	)
}

function Navigation({ items, currentItem, handleSetCurrentItem }) {
	return (
		<StyledNavigation>
			{items.map((item, index) => {
				const key = item?.id || index
				return (
					<StyledSingleNavigationItem
						key={key}
						className={`typo-borna typo-borna-5 ${currentItem.id === item.id ? "active" : ""}`}
						onClick={() => handleSetCurrentItem(item)}
					>
						<span className="number">{index + 1}</span>
					</StyledSingleNavigationItem>
				)
			})}
		</StyledNavigation>
	)
}
